import type { ApiResponse } from "../../../../../services/apiService";
import {
  EdiObject,
  ManualPaymentFields,
  ManualPaymentOutcome,
  PayerTier,
} from "../../../../../types/claims";
import { formatDisplayDate } from "../../../../helpers/dataHelper";
import {
  ClaimEditError,
  PAYER_TIER_LABELS,
  formatFeeAmount,
  parseClaimEditError,
} from "../claimAppeals";

// Contract: app-backend api/services/manual_payments.py (REV-2582, REV-2593 optional document)

export const OUTCOME_LABELS: Record<ManualPaymentOutcome, string> = {
  paid: "Paid",
  denied: "Denied",
};

// The 835 CLP02 codes a manual payment is stored with, read back as a payer tier
const TIER_BY_CLP02: Record<string, PayerTier> = {
  "1": "primary",
  "4": "primary",
  "19": "primary",
  "2": "secondary",
  "20": "secondary",
  "3": "tertiary",
  "21": "tertiary",
};
const PRIMARY_CLP02_CODES = ["1", "4", "19"];

export const isManualPayment = (ediObj: EdiObject): boolean =>
  ediObj.objectType === "PAYMENT" && ediObj.source === "manual";

/** The payer tier of any payment row (835 or manual), from its CLP02 code; null when it has none (e.g. a reversal). */
export const getPaymentPayerTier = (ediObj: EdiObject): PayerTier | null =>
  TIER_BY_CLP02[String(ediObj.claimStatusCode ?? "")] ?? null;

/** Whether the claim has an active primary remit, which a secondary or tertiary payment needs. */
export const hasPrimaryRemit = (ediObjects: EdiObject[]): boolean =>
  ediObjects.some(
    (ediObj) =>
      ediObj.objectType === "PAYMENT" &&
      PRIMARY_CLP02_CODES.includes(String(ediObj.claimStatusCode ?? "")),
  );

/** A manual payment's fields, read back from the minimal 835 it is stored as. */
export const getManualPaymentFields = (
  ediObj: EdiObject,
): ManualPaymentFields => {
  const obj = ediObj as Record<string, unknown>;
  const transaction = (obj.transaction ?? {}) as Record<string, unknown>;
  const payer = (obj.payer ?? {}) as Record<string, unknown>;
  const adjustments = (
    Array.isArray(obj.adjustments) ? obj.adjustments : []
  ) as Record<string, unknown>[];
  const responsibility = adjustments.find(
    (adjustment) => adjustment.group === "PATIENT_RESPONSIBILITY",
  );
  const amount = String(obj.paymentAmount ?? "");

  return {
    outcome: Number(amount) > 0 ? "paid" : "denied",
    payerTier: getPaymentPayerTier(ediObj) ?? "primary",
    amount,
    paymentDate: String(transaction.paymentDate ?? ""),
    payerName: String(payer.lastNameOrOrgName ?? ""),
    checkOrEftNumber: String(transaction.checkOrEftTraceNumber ?? ""),
    payerClaimNumber: String(obj.payerControlNumber ?? ""),
    patientResponsibility: String(responsibility?.amount ?? ""),
  };
};

/** Label/value rows describing a manual payment. Fields the letter didn't show are left out. */
export const describeManualPayment = (
  fields: ManualPaymentFields,
): { label: string; value: string }[] => {
  const rows = [
    { label: "Outcome", value: OUTCOME_LABELS[fields.outcome] },
    { label: "Payer", value: PAYER_TIER_LABELS[fields.payerTier] },
    { label: "Payer name", value: fields.payerName },
    {
      label: "Amount",
      value: fields.outcome === "paid" ? formatFeeAmount(fields.amount) : "",
    },
    {
      label: fields.outcome === "paid" ? "Date paid" : "Date denied",
      value: fields.paymentDate ? formatDisplayDate(fields.paymentDate) : "",
    },
    { label: "Check / EFT #", value: fields.checkOrEftNumber },
    { label: "Payer claim # (ICN)", value: fields.payerClaimNumber },
    {
      label: "Patient responsibility",
      value:
        Number(fields.patientResponsibility) > 0
          ? formatFeeAmount(fields.patientResponsibility)
          : "",
    },
  ];
  return rows.filter((row) => row.value.trim());
};

const MONEY_PATTERN = /^\d+(\.\d{1,2})?$/;

export type ManualPaymentFormField =
  | keyof ManualPaymentFields
  | "document"
  | "timezone";
export type ManualPaymentFieldErrors = Partial<
  Record<ManualPaymentFormField, string>
>;

/** The same rules the API applies (validate_manual_payment), checked as the form is filled in. */
export const validateManualPaymentForm = (
  fields: ManualPaymentFields,
  context: { today: string; primaryRemit: boolean },
): ManualPaymentFieldErrors => {
  const errors: ManualPaymentFieldErrors = {};

  if (fields.payerTier !== "primary" && !context.primaryRemit) {
    errors.payerTier = `The claim has no primary remit, so a ${fields.payerTier} payment can't be recorded.`;
  }

  const amount = fields.amount.trim();
  if (!amount) {
    errors.amount = "Enter the amount.";
  } else if (!MONEY_PATTERN.test(amount)) {
    errors.amount = "Enter an amount like 250.00.";
  } else if (fields.outcome === "paid" && Number(amount) === 0) {
    errors.amount = "Must be more than 0 for a paid outcome.";
  } else if (fields.outcome === "denied" && Number(amount) !== 0) {
    errors.amount = "Must be 0 for a denied outcome.";
  }

  if (!fields.paymentDate) {
    errors.paymentDate = "Enter the date on the letter.";
  } else if (fields.paymentDate > context.today) {
    errors.paymentDate = "Can't be in the future.";
  }

  if (!fields.payerName.trim()) {
    errors.payerName = "Enter the payer's name.";
  }

  const responsibility = fields.patientResponsibility.trim();
  if (responsibility && !MONEY_PATTERN.test(responsibility)) {
    errors.patientResponsibility = "Enter an amount like 25.00.";
  }

  return errors;
};

export type RecordPaymentErrorCode =
  | "validation_error"
  | "duplicate_payment"
  | "claim_invoiced";

export type RemovePaymentErrorCode =
  | "validation_error"
  | "not_found"
  | "payment_not_active"
  | "claim_invoiced";

const RECORD_FIELDS: ManualPaymentFormField[] = [
  "outcome",
  "payerTier",
  "amount",
  "paymentDate",
  "payerName",
  "checkOrEftNumber",
  "payerClaimNumber",
  "patientResponsibility",
  "document",
  "timezone",
];

export const parseRecordPaymentError = (
  response: ApiResponse<unknown>,
): ClaimEditError<RecordPaymentErrorCode, ManualPaymentFormField> =>
  parseClaimEditError(
    response,
    ["validation_error", "duplicate_payment", "claim_invoiced"],
    RECORD_FIELDS,
  );

export const parseRemovePaymentError = (
  response: ApiResponse<unknown>,
): ClaimEditError<RemovePaymentErrorCode, "reason"> =>
  parseClaimEditError(
    response,
    ["validation_error", "not_found", "payment_not_active", "claim_invoiced"],
    ["reason"],
  );
